import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '#/components/Button/Button';
import { closeDropdownFromElementWithin } from '#/components/Dropdown/closeDropdownFromElementWithin';
import { LocationInput } from '#/components/LocationInput/LocationInput';
import type { LocationRadiusSelectHandle } from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { LocationRadiusSelect } from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { Text } from '#/components/Text/Text';
import { UncontrolledSearchQueryInputContainer } from '#/containers/UncontrolledSearchQueryInputContainer';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { UncontrolledListingTypeSelect } from '#/layouts/Layout/ListingTypeSelect/UncontrolledListingTypeSelect';
import { SearchButton } from '#/modules/search/components/SearchButton';
import { searchTypes } from '#/modules/search/constants/searchTypes';
import { isPointGeotype } from '#/modules/search/helpers/isPointGeotype';
import { getPopularSearchesBySearchType } from '#/modules/search/mappings/getPopularSearchesBySearchType';
import { searchGetPathSwitch } from '#/modules/search/routing/helpers/searchGetPathSwitch';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { colors } from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import {
  MobileSearchMenuBody,
  MobileSearchMenuFooter,
  MobileSearchMenuForm,
  MobileSearchMenuHeader,
  MobileSearchMenuHolder,
} from './MobileSearchMenu.styled';
import { MobileSearchMenuPopularSearches } from './PopularSearches/MobileSearchMenuPopularSearches';

type Props = {
  defaultSearchType: SearchType;
  initialQuery: string;
  searchLocation: SearchLocation | undefined;
  onChangeSearchLocation: (
    newSearchLocation: SearchLocation | undefined,
  ) => boolean;
  radiusOptions: { label: string; value: string }[];
  radiusHandle: LocationRadiusSelectHandle;
  onSearch: (data: {
    listingType: SearchType;
    query: string;
    radius: string;
    triggeredByRemoteLocationChange: boolean;
  }) => void;
  searchOnClear: boolean;
  onClose?: () => void;
};

export function MobileSearchMenu({
  defaultSearchType,
  initialQuery,
  searchLocation,
  onChangeSearchLocation,
  radiusHandle,
  radiusOptions,
  onSearch,
  searchOnClear,
  onClose,
}: Props) {
  const { i18n, locale } = useI18n();
  const navigate = useNavigate();

  const [searchTypeForPopularSearches, setSearchTypeForPopularSearches] =
    useKeyedState(defaultSearchType);

  const clearSearch = () => {
    void navigate(
      searchGetPathSwitch({
        filtersByName: { type: defaultSearchType },
        locale,
        embedSearchWidgetId: null,
      }),
    );
  };

  const popularSearches = getPopularSearchesBySearchType(
    i18n,
    searchTypeForPopularSearches,
  );

  const formRef = useRef<HTMLFormElement>(null);
  const submit = useCallback(
    (options: { triggeredByRemoteLocationChange: boolean }) => {
      const form = formRef.current;
      if (!form) return;

      const formData = new FormData(form);
      onSearch({
        query: formData.get('q') as string,
        listingType: formData.get('listing-type') as SearchType,
        triggeredByRemoteLocationChange:
          options.triggeredByRemoteLocationChange,
        radius: formData.get('radius') as string,
      });

      if (onClose) {
        onClose();
      } else {
        closeDropdownFromElementWithin(form);
      }
    },
    [onSearch, onClose],
  );

  return (
    <MobileSearchMenuHolder data-qa-id="mobile-search-menu">
      <MobileSearchMenuHeader>
        <Text
          tag="div"
          styleAs="h4"
          style={{ marginBottom: 0, color: colors.brandBlue }}
        >
          {i18n('Search')}
        </Text>

        <Button
          type="button"
          qaId="close-mobile-search"
          onClick={(event) => {
            if (onClose) {
              onClose();
            } else {
              closeDropdownFromElementWithin(event.target as HTMLElement);
            }
          }}
          aria-label={i18n('Close search')}
          variant="tertiary"
          noPadding
          icon={{ name: 'close' }}
        />
      </MobileSearchMenuHeader>

      <MobileSearchMenuForm
        ref={formRef}
        onSubmit={(event) => {
          event.preventDefault();
          submit({ triggeredByRemoteLocationChange: false });
        }}
        data-qa-id="search-inputs"
      >
        <MobileSearchMenuBody>
          <UncontrolledListingTypeSelect
            name="listing-type"
            defaultValue={defaultSearchType}
            searchTypes={searchTypes}
            variant="grey"
            onChange={(listingType) => {
              trackEvent('Changed Search Type', {
                filter_type: listingType,
                context: 'Mobile Search Menu',
              });
              setSearchTypeForPopularSearches(listingType);
            }}
          />

          <UncontrolledSearchQueryInputContainer
            name="q"
            initialQuery={initialQuery}
            placeholder={i18n('Search by keyword, skill, or interest')}
            onClearQuery={
              searchOnClear
                ? () => submit({ triggeredByRemoteLocationChange: false })
                : undefined
            }
            presentation="contrast"
            id="mobile-search-query"
            qaId="mobile-search-input"
            searchType={defaultSearchType}
          />

          <LocationInput
            affixVariant="transparent"
            styleVariant="contrast"
            hasRemoteOptions
            showLocationIcon
            showClearButton
            placeholder={i18n('Everywhere')}
            locationText={searchLocation?.text}
            onChange={(newLocation) => {
              // Avoid submitting twice - the LocationInput has a bug that triggers the onChange twice
              if (!onChangeSearchLocation(newLocation)) return;

              // Trigger search when search location changes
              submit({
                triggeredByRemoteLocationChange: Boolean(newLocation?.isRemote),
              });
            }}
            id="page-header-mobile-search-location"
          />

          <LocationRadiusSelect
            handle={radiusHandle}
            options={radiusOptions}
            disabled={
              !searchLocation || !isPointGeotype(searchLocation.geoType)
            }
            fullWidth
            qaId="page-header-mobile-location-radius"
          />
        </MobileSearchMenuBody>

        <MobileSearchMenuPopularSearches
          searchType={searchTypeForPopularSearches}
          popularSearches={popularSearches}
          onSearch={(event, query) => {
            if (onClose) {
              onClose();
            } else {
              closeDropdownFromElementWithin(event.target as HTMLElement);
            }
            onSearch({
              listingType: searchTypeForPopularSearches,
              query,
              radius: radiusHandle.radius,
              triggeredByRemoteLocationChange: false,
            });
          }}
        />

        <MobileSearchMenuFooter>
          <Button
            variant="tertiary"
            type="button"
            onClick={() => {
              clearSearch();
            }}
            width={192}
          >
            {i18n('Clear Search')}
          </Button>

          <div style={{ width: 192 }}>
            <SearchButton />
          </div>
        </MobileSearchMenuFooter>
      </MobileSearchMenuForm>
    </MobileSearchMenuHolder>
  );
}
